import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { LayerResults } from "../../components/kyc/LayerResults";
import { VersionLista } from "../../components/screening/ScreeningResults";
import { DatosTab, RiesgoTab, HistorialTab, Acciones, DdrForm } from "../../components/cliente/FichaParts";
import { NIVEL, ESTADO_STYLE } from "./Clientes";

const TIPO_AML = { sancion: "Sanción", pep: "PEP", prensa_negativa: "Prensa negativa", lista_vigilancia: "Lista de vigilancia" };

const AmlDidit = ({ a }) => {
  if (!a) return null;
  return (
    <div className="border-t pt-2 space-y-1" data-testid="ficha-aml-didit">
      <p><b className="uppercase">Didit</b> · AML (sanciones, PEP, prensa negativa) · {a.estado} · puntaje {a.puntaje ?? "—"} · {fmtFecha(a.actualizado)}{a.monitoreo && " · monitoreo diario activo"}</p>
      {(a.coincidencias || []).map((h) => (
        <p key={h.id} className={`pl-3 ${h.revision_didit === "False Positive" ? "text-slate-400" : "text-amber-800"}`} data-testid="ficha-aml-didit-hit">
          {TIPO_AML[h.tipo] || h.tipo} · {h.lista || "—"} · {h.nombre} · {h.pais || "—"} · coincidencia {h.puntaje ?? "—"} · riesgo {h.riesgo ?? "—"}{h.revision_didit && ` · ${h.revision_didit}`}
        </p>
      ))}
    </div>
  );
};

const ListasTab = ({ c }) => (
  <div className="space-y-1 text-xs" data-testid="ficha-tab-listas">
    {c.aviso_pep && <p className="text-amber-700 text-sm">{c.aviso_pep}</p>}
    {(c.screening || []).map((r) => (
      <p key={r.id} className="border-b border-slate-100 pb-1"><b className="uppercase">IntiCheck</b> · <b className="uppercase">{r.fuente}</b> · {r.resultado} · {fmtFecha(r.fecha)} · {r.motivo} · <VersionLista v={r.version_lista} />
        {r.coincidencias.length > 0 && ` · ${r.coincidencias.length} coincidencia(s)`}{r.revision && ` · revisado: ${r.revision.decision}`}</p>
    ))}
    <AmlDidit a={c.aml_didit} />
    <p className="text-slate-500">Datos PEP: Fuente: InfoProbidad, Consejo para la Transparencia.</p>
  </div>
);

export default function ClienteFicha() {
  const { id } = useParams();
  const { me } = useAuth();
  const rol = me.usuario.rol === "owner" && me.usuario.tambien_oficial ? "owner_oficial" : me.usuario.rol;
  const [c, setC] = useState(null);
  const load = useCallback(() => {
    api.get(`/company/clientes/${id}`).then((r) => setC(r.data));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  const pdf = async () => {
    const r = await api.get(`/company/export/clientes/${id}.pdf`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `ficha-${id.slice(0, 8)}.pdf`;
    a.click();
  };
  if (!c) return null;
  const manager = rol !== "operator";
  return (
    <div className="space-y-6" data-testid="client-file">
      <Link to="/panel/clientes" className="text-sm underline">← Mis clientes</Link>
      <PageHeader overline="Ficha del cliente" title={`${c.nombres} ${c.apellido_paterno} ${c.apellido_materno || ""}`}>
        <StatusBadge value={ESTADO_STYLE[c.estado]} label={c.estado} testId="ficha-estado" />
        {c.riesgo && <StatusBadge value={NIVEL[c.riesgo.nivel]} label={`riesgo ${c.riesgo.nivel}`} />}
        {manager && <Button variant="outline" onClick={pdf} data-testid="export-pdf-button">Exportar PDF</Button>}
      </PageHeader>
      <Tabs defaultValue="datos">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="datos" data-testid="tab-datos">Datos y documento</TabsTrigger>
          {manager && <TabsTrigger value="identidad" data-testid="tab-identidad">Verificación de identidad</TabsTrigger>}
          {manager && <TabsTrigger value="listas" data-testid="tab-listas">Listas y PEP</TabsTrigger>}
          {manager && <TabsTrigger value="riesgo" data-testid="tab-riesgo">Riesgo</TabsTrigger>}
          <TabsTrigger value="historial" data-testid="tab-historial">Historial</TabsTrigger>
        </TabsList>
        <TabsContent value="datos"><DatosTab c={c} rol={rol} /></TabsContent>
        {manager && <TabsContent value="identidad">{c.sesiones[0] ? <LayerResults s={c.sesiones[0]} /> : <p className="text-sm">Sin verificación.</p>}</TabsContent>}
        {manager && <TabsContent value="listas"><ListasTab c={c} /></TabsContent>}
        {manager && <TabsContent value="riesgo"><RiesgoTab c={c} />{["compliance_officer", "owner_oficial"].includes(rol) && !c.ddr && <div className="mt-4"><b className="text-sm">Diligencia reforzada</b><DdrForm c={c} onDone={load} /></div>}</TabsContent>}
        <TabsContent value="historial"><HistorialTab c={c} /></TabsContent>
      </Tabs>
      {!["bloqueado"].includes(c.estado) && <Acciones c={c} rol={rol} onDone={load} />}
    </div>
  );
}
